/* eslint-disable react/no-unknown-property */
/* ============================================================
   Space objects – satellites (3 realistic variants), ISS,
   alien saucer (with pilot) and noise-displaced asteroids.
   Behaviour (orbits, lasers, collisions) is unchanged from the
   original implementation – only the visuals were rebuilt.
   ============================================================ */
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Billboard, Text } from "@react-three/drei";
import * as THREE from "three";
import { useWorldStore } from "@/components/world/store";
import { getSolarTexture, getBrushedMetalTexture, getStoneTexture, makeNoise3D } from "@/components/world/textures";
import { latticeTruss, mergeAll, barBetween } from "@/components/world/models/geometryUtils";
import { TinyAlien } from "@/components/world/models/Characters";

/* ---------- shared textures / materials (lazy) ---------- */
let _mats = null;
function mats() {
  if (_mats) return _mats;
  const solar = getSolarTexture();
  const foil = getStoneTexture("#C9A44E", 3, 48);
  if (foil) foil.repeat.set(2, 2);
  const brushed = getBrushedMetalTexture("#A9B0B8", 21);
  if (brushed) brushed.repeat.set(6, 1);
  _mats = {
    // NOTE: no environment map in the scene, so metalness is kept moderate –
    // fully metallic surfaces would render almost black in deep space.
    solar: new THREE.MeshStandardMaterial({ map: solar || undefined, color: solar ? "#FFFFFF" : "#13306A", metalness: 0.25, roughness: 0.35, emissive: "#1B3A7A", emissiveIntensity: 0.25, side: THREE.DoubleSide }),
    solarBack: new THREE.MeshStandardMaterial({ color: "#4A5060", metalness: 0.3, roughness: 0.6 }),
    foil: new THREE.MeshStandardMaterial({ map: foil || undefined, color: foil ? "#FFFFFF" : "#C9A44E", metalness: 0.45, roughness: 0.45, emissive: "#3A2A08", emissiveIntensity: 0.25 }),
    white: new THREE.MeshStandardMaterial({ color: "#E9E5DD", metalness: 0.15, roughness: 0.5, emissive: "#2A2C33", emissiveIntensity: 0.3 }),
    silver: new THREE.MeshStandardMaterial({ color: "#C8CCD2", metalness: 0.5, roughness: 0.35, emissive: "#22262E", emissiveIntensity: 0.35 }),
    dark: new THREE.MeshStandardMaterial({ color: "#2A2E36", metalness: 0.4, roughness: 0.5, emissive: "#0E1016", emissiveIntensity: 0.4 }),
    truss: new THREE.MeshStandardMaterial({ color: "#C4C7CC", metalness: 0.3, roughness: 0.5, emissive: "#2A2C33", emissiveIntensity: 0.35 }),
    radiator: new THREE.MeshStandardMaterial({ color: "#F4F2EE", metalness: 0.15, roughness: 0.3, emissive: "#33363D", emissiveIntensity: 0.3, side: THREE.DoubleSide }),
    hull: new THREE.MeshStandardMaterial({ map: brushed || undefined, color: brushed ? "#FFFFFF" : "#A9B0B8", metalness: 0.55, roughness: 0.32, emissive: "#22343A", emissiveIntensity: 0.3 }),
    glass: new THREE.MeshStandardMaterial({ color: "#9FE9FF", transparent: true, opacity: 0.3, metalness: 0.2, roughness: 0.05, emissive: "#1E5A3C", emissiveIntensity: 0.3, side: THREE.DoubleSide, depthWrite: false }),
    rock: [
      new THREE.MeshStandardMaterial({ color: "#8A7C72", roughness: 1, metalness: 0.05, flatShading: true, emissive: "#2A2420", emissiveIntensity: 0.6 }),
      new THREE.MeshStandardMaterial({ color: "#76706F", roughness: 1, metalness: 0.08, flatShading: true, emissive: "#231F22", emissiveIntensity: 0.6 }),
      new THREE.MeshStandardMaterial({ color: "#9A8873", roughness: 0.95, metalness: 0.03, flatShading: true, emissive: "#2C241C", emissiveIntensity: 0.6 }),
    ],
  };
  return _mats;
}

/* ---------- geometry ---------- */
const DISH_GEO = (() => {
  const pts = [];
  for (let i = 0; i <= 12; i++) {
    const r = (i / 12) * 0.9;
    pts.push(new THREE.Vector2(Math.max(0.001, r), 0.42 * r * r));
  }
  const g = new THREE.LatheGeometry(pts, 28);
  g.computeVertexNormals();
  return g;
})();

const ASTEROID_GEOS = (() => {
  const { fbm } = makeNoise3D(5);
  const v = new THREE.Vector3();
  return [0, 1, 2].map((k) => {
    const g = new THREE.IcosahedronGeometry(1, 2);
    const pos = g.attributes.position;
    const off = k * 7.3;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      const n = fbm(v.x * 1.3 + off, v.y * 1.3 + off, v.z * 1.3 + off, 4);
      const n2 = fbm(v.x * 4 + off, v.y * 4, v.z * 4, 2);
      const r = 0.72 + n * 0.6 + (n2 - 0.5) * 0.14;
      v.multiplyScalar(r);
      v.x *= 1 + k * 0.18;
      v.z *= 1 - k * 0.06;
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    g.computeVertexNormals();
    return g;
  });
})();

const ISS_TRUSS_GEO = latticeTruss(9.2, 0.55, 16, 0.045);

const SAUCER_GEO = (() => {
  const pts = [
    [0.001, -0.55],
    [1.1, -0.62],
    [2.4, -0.55],
    [3.6, -0.35],
    [4.5, -0.1],
    [4.85, 0.05],
    [4.9, 0.15],
    [4.6, 0.32],
    [3.9, 0.48],
    [3.0, 0.62],
    [2.2, 0.76],
    [2.05, 0.85],
    [0.001, 0.86],
  ].map(([r, y]) => new THREE.Vector2(r, y));
  const g = new THREE.LatheGeometry(pts, 48);
  g.computeVertexNormals();
  return g;
})();

/* ============================================================
   Satellite
   ============================================================ */
function SolarWing({ position, rotation = [0, 0, 0], w = 3.4, h = 1.2, boom = 0.7, dir = 1 }) {
  const M = mats();
  return (
    <group position={position} rotation={rotation}>
      <mesh position={[(dir * boom) / 2, 0, 0]} rotation={[0, 0, Math.PI / 2]} material={M.silver}>
        <cylinderGeometry args={[0.04, 0.04, boom, 8]} />
      </mesh>
      <group position={[dir * (boom + w / 2), 0, 0]}>
        <mesh material={M.solar} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[w, h]} />
        </mesh>
        <mesh material={M.solarBack} position={[0, -0.02, 0]}>
          <boxGeometry args={[w + 0.06, 0.03, h + 0.06]} />
        </mesh>
        {/* panel frame rails */}
        <mesh material={M.silver} position={[0, 0.005, 0]}>
          <boxGeometry args={[w + 0.08, 0.02, 0.04]} />
        </mesh>
      </group>
    </group>
  );
}

function CommsSat({ blinkRef }) {
  const M = mats();
  return (
    <>
      <mesh material={M.foil}>
        <boxGeometry args={[1.2, 1.2, 1.6]} />
      </mesh>
      {/* radiator side plates */}
      <mesh material={M.white} position={[0, 0, 0.82]}>
        <boxGeometry args={[1.1, 1.1, 0.04]} />
      </mesh>
      <SolarWing position={[0.6, 0, 0]} dir={1} />
      <SolarWing position={[-0.6, 0, 0]} dir={-1} />
      {/* big dish on a strut, pointing down toward the planet */}
      <mesh material={M.silver} position={[0, 0.85, 0]}>
        <cylinderGeometry args={[0.05, 0.05, 0.5, 8]} />
      </mesh>
      <group position={[0, 1.1, 0.1]} rotation={[-0.55, 0, 0]}>
        <mesh geometry={DISH_GEO} material={M.white} />
        <mesh geometry={DISH_GEO} material={M.dark} scale={[0.98, 0.98, 0.98]} position={[0, -0.01, 0]} />
        <mesh material={M.silver} position={[0, 0.35, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 0.7, 6]} />
        </mesh>
        <mesh material={M.dark} position={[0, 0.72, 0]} rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.08, 0.14, 8]} />
        </mesh>
      </group>
      {/* small omni antennas */}
      {[[0.45, 0.4], [-0.45, 0.4]].map(([ax, az], i) => (
        <mesh key={i} material={M.silver} position={[ax, -0.95, az]}>
          <cylinderGeometry args={[0.012, 0.012, 0.7, 6]} />
        </mesh>
      ))}
      {/* thruster nozzles */}
      {[[0.4, 0.4], [-0.4, 0.4], [0.4, -0.4], [-0.4, -0.4]].map(([tx, ty], i) => (
        <mesh key={i} material={M.dark} position={[tx, ty, -0.9]} rotation={[-Math.PI / 2, 0, 0]}>
          <coneGeometry args={[0.09, 0.2, 10, 1, true]} />
        </mesh>
      ))}
      <mesh ref={blinkRef} position={[0, -0.66, 0.5]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial color="#E94B4B" emissive="#E94B4B" emissiveIntensity={1.4} toneMapped={false} />
      </mesh>
    </>
  );
}

function TelescopeSat({ blinkRef }) {
  const M = mats();
  return (
    <>
      {/* optical tube */}
      <mesh material={M.silver} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, -0.5]}>
        <cylinderGeometry args={[0.55, 0.55, 2.4, 24]} />
      </mesh>
      {/* aft equipment section (wider) */}
      <mesh material={M.silver} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 1.2]}>
        <cylinderGeometry args={[0.65, 0.65, 1.3, 24]} />
      </mesh>
      {/* MLI bands */}
      {[-1.3, -0.2, 0.6].map((z, i) => (
        <mesh key={i} material={M.foil} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, z]}>
          <cylinderGeometry args={[0.575, 0.575, 0.22, 24]} />
        </mesh>
      ))}
      {/* aperture door (open) */}
      <mesh material={M.silver} position={[0, 0.55, -1.75]} rotation={[-1.1, 0, 0]}>
        <cylinderGeometry args={[0.56, 0.56, 0.04, 24]} />
      </mesh>
      {/* aperture interior */}
      <mesh position={[0, 0, -1.71]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.5, 0.5, 0.02, 24]} />
        <meshStandardMaterial color="#05070A" roughness={0.9} />
      </mesh>
      <SolarWing position={[0.65, 0, 0.2]} dir={1} w={2.6} h={0.95} boom={0.5} />
      <SolarWing position={[-0.65, 0, 0.2]} dir={-1} w={2.6} h={0.95} boom={0.5} />
      {/* high-gain antennas */}
      {[1, -1].map((s, i) => (
        <group key={i} position={[0, s * 0.9, 0.9]}>
          <mesh material={M.silver}>
            <cylinderGeometry args={[0.025, 0.025, 0.6, 6]} />
          </mesh>
          <mesh geometry={DISH_GEO} material={M.white} scale={0.45} position={[0, s * 0.35, 0]} rotation={[s > 0 ? 0 : Math.PI, 0, 0]} />
        </group>
      ))}
      <mesh ref={blinkRef} position={[0.6, 0.35, 1.2]}>
        <sphereGeometry args={[0.07, 8, 8]} />
        <meshStandardMaterial color="#5AFF8A" emissive="#5AFF8A" emissiveIntensity={1.4} toneMapped={false} />
      </mesh>
    </>
  );
}

function ObservationSat({ blinkRef }) {
  const M = mats();
  return (
    <>
      <mesh material={M.white}>
        <boxGeometry args={[1.0, 0.9, 1.5]} />
      </mesh>
      <mesh material={M.foil} position={[0, 0, -0.2]}>
        <boxGeometry args={[1.04, 0.94, 0.5]} />
      </mesh>
      {/* two-panel wings */}
      <SolarWing position={[0.5, 0.2, 0]} dir={1} w={2.2} h={1.0} boom={0.4} />
      <SolarWing position={[-0.5, 0.2, 0]} dir={-1} w={2.2} h={1.0} boom={0.4} />
      {/* nadir camera */}
      <mesh material={M.dark} position={[0, -0.7, 0.3]}>
        <cylinderGeometry args={[0.18, 0.28, 0.5, 14]} />
      </mesh>
      <mesh position={[0, -0.96, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.16, 14]} />
        <meshStandardMaterial color="#1A3A5A" metalness={0.9} roughness={0.05} />
      </mesh>
      {/* SAR radar panel */}
      <mesh material={M.dark} position={[0, -0.55, -0.35]} rotation={[0.35, 0, 0]}>
        <boxGeometry args={[2.6, 0.05, 0.7]} />
      </mesh>
      {/* magnetometer boom */}
      <mesh material={M.silver} position={[0, 0.45, 1.6]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 1.8, 6]} />
      </mesh>
      <mesh material={M.dark} position={[0, 0.45, 2.55]}>
        <sphereGeometry args={[0.09, 8, 8]} />
      </mesh>
      <mesh ref={blinkRef} position={[0, 0.5, 0.75]}>
        <sphereGeometry args={[0.07, 8, 8]} />
        <meshStandardMaterial color="#F0A020" emissive="#F0A020" emissiveIntensity={1.4} toneMapped={false} />
      </mesh>
    </>
  );
}

export function Satellite({ data }) {
  const groupRef = useRef();
  const blinkRef = useRef();
  const variant = data.seed % 3;
  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    groupRef.current.rotation.y = t * data.speed;
    groupRef.current.position.x = data.pos[0] + Math.sin(t * 0.05 + data.seed) * 8;
    groupRef.current.position.y = data.pos[1] + Math.cos(t * 0.06 + data.seed) * 3;
    groupRef.current.position.z = data.pos[2] + Math.cos(t * 0.04 + data.seed) * 8;
    if (blinkRef.current) {
      blinkRef.current.material.emissiveIntensity = (t * 1.1 + data.seed) % 1 < 0.15 ? 3 : 0.15;
    }
  });
  return (
    <group ref={groupRef} rotation={[data.tilt, 0, 0]} scale={1.15}>
      {variant === 0 && <CommsSat blinkRef={blinkRef} />}
      {variant === 1 && <TelescopeSat blinkRef={blinkRef} />}
      {variant === 2 && <ObservationSat blinkRef={blinkRef} />}
    </group>
  );
}

/* ============================================================
   ISS – integrated truss, 8 solar array wings, radiators,
   pressurised module cluster, Soyuz + Dragon docked, Canadarm2
   ============================================================ */
function Module({ position = [0, 0, 0], rotation = [0, 0, 0], r = 0.3, len = 1.2, material, foilBands = 0 }) {
  const M = mats();
  return (
    <group position={position} rotation={rotation}>
      <mesh material={material || M.white}>
        <cylinderGeometry args={[r, r, len, 16]} />
      </mesh>
      <mesh material={material || M.white} position={[0, len / 2, 0]}>
        <sphereGeometry args={[r, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
      </mesh>
      <mesh material={material || M.white} position={[0, -len / 2, 0]} rotation={[Math.PI, 0, 0]}>
        <sphereGeometry args={[r, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
      </mesh>
      {[...Array(foilBands)].map((_, i) => (
        <mesh key={i} material={M.foil} position={[0, -len / 2 + ((i + 1) * len) / (foilBands + 1), 0]}>
          <cylinderGeometry args={[r + 0.012, r + 0.012, 0.12, 16]} />
        </mesh>
      ))}
    </group>
  );
}

export function ISS({ orbitCenter, orbitRadius, orbitSpeed }) {
  const ref = useRef();
  const panelRef = useRef();
  const armRef = useRef();
  const M = mats();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    const a = t * orbitSpeed;
    ref.current.position.x = orbitCenter[0] + Math.cos(a) * orbitRadius;
    ref.current.position.y = orbitCenter[1] + Math.sin(a * 0.3) * 4;
    ref.current.position.z = orbitCenter[2] + Math.sin(a) * orbitRadius;
    ref.current.rotation.y = -a + Math.PI / 2;
    if (panelRef.current) panelRef.current.rotation.x = Math.sin(t * 0.25) * 0.35;
    if (armRef.current) armRef.current.rotation.z = 0.6 + Math.sin(t * 0.3) * 0.25;
  });
  const wingX = [-4.0, -2.6, 2.6, 4.0];
  return (
    <group ref={ref} scale={1.25}>
      {/* main truss */}
      <mesh geometry={ISS_TRUSS_GEO} material={M.truss} position={[0, 0.55, 0]} />
      {/* solar array wings on alpha rotary joints */}
      <group ref={panelRef} position={[0, 0.55, 0]}>
        {wingX.map((x, i) => (
          <group key={i} position={[x, 0, 0]}>
            <mesh material={M.silver} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.2, 0.2, 0.5, 12]} />
            </mesh>
            {[1, -1].map((s) => (
              <group key={s} position={[0, 0, s * 2.05]}>
                {/* mast */}
                <mesh material={M.silver} rotation={[Math.PI / 2, 0, 0]}>
                  <cylinderGeometry args={[0.035, 0.035, 3.5, 6]} />
                </mesh>
                {/* twin blankets */}
                {[0.32, -0.32].map((ox) => (
                  <mesh key={ox} material={M.solar} position={[ox, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                    <planeGeometry args={[0.55, 3.4]} />
                  </mesh>
                ))}
              </group>
            ))}
          </group>
        ))}
      </group>
      {/* thermal radiators hanging from the truss */}
      {[-1.7, -1.2, 1.2, 1.7].map((x, i) => (
        <mesh key={i} material={M.radiator} position={[x, -0.55, -0.9]} rotation={[0, 0, 0]}>
          <boxGeometry args={[0.38, 1.9, 0.03]} />
        </mesh>
      ))}
      {/* Z1 truss connecting the module cluster to the main truss */}
      <mesh material={M.truss} position={[0.3, 0.28, 0]}>
        <boxGeometry args={[0.5, 0.35, 0.5]} />
      </mesh>
      {/* pressurised modules (chain along Z) */}
      <Module position={[0, 0, -2.4]} rotation={[Math.PI / 2, 0, 0]} r={0.26} len={1.3} material={M.foil} />
      <Module position={[0, 0, -1.2]} rotation={[Math.PI / 2, 0, 0]} r={0.3} len={1.1} material={M.foil} foilBands={2} />
      <Module position={[0.3, 0, -0.1]} rotation={[Math.PI / 2, 0, 0]} r={0.3} len={0.9} />
      <Module position={[0.3, 0, 1.0]} rotation={[Math.PI / 2, 0, 0]} r={0.31} len={1.2} foilBands={1} />
      <Module position={[0.3, 0, 2.05]} rotation={[Math.PI / 2, 0, 0]} r={0.3} len={0.8} />
      {/* Columbus (+x) and Kibo (-x) on the forward node */}
      <Module position={[1.05, 0, 2.05]} rotation={[0, 0, Math.PI / 2]} r={0.27} len={0.9} />
      <Module position={[-0.55, 0, 2.05]} rotation={[0, 0, Math.PI / 2]} r={0.29} len={1.1} />
      {/* Node 3 + Cupola under Unity */}
      <Module position={[0.3, -0.6, -0.1]} r={0.24} len={0.6} />
      <mesh material={M.dark} position={[0.3, -1.02, -0.1]}>
        <sphereGeometry args={[0.2, 12, 8, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2]} />
      </mesh>
      {/* Soyuz docked aft */}
      <group position={[0, 0, -3.35]}>
        <mesh material={M.foil} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.16, 0.16, 0.5, 12]} />
        </mesh>
        <mesh material={M.white} position={[0, 0, -0.42]}>
          <sphereGeometry args={[0.17, 12, 10]} />
        </mesh>
        {[0.7, -0.7].map((x) => (
          <mesh key={x} material={M.solar} position={[x, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[0.9, 0.3]} />
          </mesh>
        ))}
      </group>
      {/* Dragon docked forward */}
      <group position={[0.3, 0, 2.85]}>
        <mesh material={M.white} rotation={[-Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.16, 0.26, 0.5, 14]} />
        </mesh>
        <mesh material={M.dark} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, -0.3]}>
          <cylinderGeometry args={[0.26, 0.26, 0.3, 14]} />
        </mesh>
      </group>
      {/* Canadarm2 */}
      <group position={[-0.6, 0.85, 0.3]} rotation={[0, 0, 0.6]} ref={armRef}>
        <mesh material={M.white} position={[0, 0.6, 0]}>
          <cylinderGeometry args={[0.045, 0.045, 1.2, 8]} />
        </mesh>
        <group position={[0, 1.2, 0]} rotation={[0, 0, -1.4]}>
          <mesh material={M.white} position={[0, 0.5, 0]}>
            <cylinderGeometry args={[0.04, 0.04, 1.0, 8]} />
          </mesh>
        </group>
      </group>
      {/* strobe */}
      <mesh position={[4.6, 0.55, 0]}>
        <sphereGeometry args={[0.07, 8, 8]} />
        <meshBasicMaterial color="#E94B4B" toneMapped={false} />
      </mesh>
      <Billboard position={[0, 2.6, 0]} follow>
        <Text fontSize={1.3} color="#EAF3FF" anchorX="center" anchorY="middle" outlineWidth={0.05} outlineColor="#020108" letterSpacing={0.24}>
          ISS
        </Text>
        <Text position={[0, -1.1, 0]} fontSize={0.5} color="#9AC5FF" anchorX="center" anchorY="middle" letterSpacing={0.32}>
          INTERNATIONAL · 408 KM
        </Text>
      </Billboard>
      <pointLight color="#9AC5FF" intensity={0.6} distance={9} />
    </group>
  );
}

/* ============================================================
   Alien saucer – orbits, faces player, fires lasers
   ============================================================ */
export function AlienShip({ debugStatic = false }) {
  const groupRef = useRef();
  const rimRef = useRef();
  const domeRef = useRef();
  const beamRef = useRef();
  const ringGlowRef = useRef();
  const laserRefs = useRef([]);
  const lasers = useRef([]); // { active, x, y, z, vx, vy, vz, bornAt }
  const nextShotAt = useRef(4);
  const M = mats();

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    const a = t * 0.05;
    const r = 180 + Math.sin(t * 0.03) * 40;
    const x = debugStatic ? 0 : Math.cos(a) * r;
    const z = debugStatic ? 0 : Math.sin(a) * r;
    const y = debugStatic ? 0 : 45 + Math.sin(t * 0.2) * 20;
    groupRef.current.position.set(x, y, z);

    const st = useWorldStore.getState();
    const px = st.position.x;
    const pz = st.position.z;
    const dx = px - x;
    const dz = pz - z;
    const facing = Math.atan2(dx, dz) + Math.PI;
    groupRef.current.rotation.y = debugStatic ? t * 0.2 : facing;
    groupRef.current.rotation.z = Math.sin(t * 0.7) * 0.06;
    groupRef.current.rotation.x = Math.cos(t * 0.5) * 0.05;

    if (rimRef.current) rimRef.current.rotation.y = t * 2.2;
    if (domeRef.current) domeRef.current.material.emissiveIntensity = 0.2 + Math.sin(t * 2.5) * 0.12;
    if (ringGlowRef.current) ringGlowRef.current.material.emissiveIntensity = 1.4 + Math.sin(t * 4) * 0.6;

    if (t > nextShotAt.current) {
      const py = st.position.y;
      const dy = py - y;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const dirX = dx / dist;
      const dirY = dy / dist;
      const dirZ = dz / dist;
      const speed = 55;
      const slot = lasers.current.find((l) => !l.active);
      const laser = slot || { active: false };
      laser.active = true;
      laser.x = x;
      laser.y = y - 0.6;
      laser.z = z;
      laser.vx = dirX * speed;
      laser.vy = dirY * speed;
      laser.vz = dirZ * speed;
      laser.bornAt = t;
      if (!slot) lasers.current.push(laser);
      nextShotAt.current = t + 2.5 + Math.random() * 3;
      if (beamRef.current) {
        beamRef.current.visible = true;
        beamRef.current.userData.flashUntil = t + 0.15;
      }
    }
    if (beamRef.current && beamRef.current.userData.flashUntil && t > beamRef.current.userData.flashUntil) {
      beamRef.current.visible = false;
    }

    lasers.current.forEach((l, i) => {
      const ref = laserRefs.current[i];
      if (!ref) return;
      if (!l.active) {
        ref.visible = false;
        return;
      }
      const age = t - l.bornAt;
      if (age > 3.5) {
        l.active = false;
        ref.visible = false;
        return;
      }
      l.x += l.vx * delta;
      l.y += l.vy * delta;
      l.z += l.vz * delta;
      ref.visible = true;
      ref.position.set(l.x, l.y, l.z);
      ref.lookAt(l.x + l.vx, l.y + l.vy, l.z + l.vz);
      const alpha = Math.max(0, 1 - age / 3.5);
      ref.children.forEach((c) => {
        if (c.material) c.material.opacity = alpha;
      });
    });
  });

  return (
    <>
      <group ref={groupRef}>
        {/* hull */}
        <mesh geometry={SAUCER_GEO} material={M.hull} />
        {/* panel seams */}
        {[3.1, 4.25].map((rr, i) => (
          <mesh key={i} position={[0, i === 0 ? 0.6 : 0.38, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[rr, 0.025, 6, 64]} />
            <meshStandardMaterial color="#3A404A" metalness={0.9} roughness={0.4} />
          </mesh>
        ))}
        {/* rim light channel */}
        <mesh ref={ringGlowRef} position={[0, 0.1, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[4.75, 0.07, 8, 72]} />
          <meshStandardMaterial color="#5EDC7F" emissive="#5EDC7F" emissiveIntensity={1.6} toneMapped={false} />
        </mesh>
        {/* rotating running lights */}
        <group ref={rimRef} position={[0, -0.05, 0]}>
          {[...Array(12)].map((_, i) => {
            const ang = (i / 12) * Math.PI * 2;
            return (
              <mesh key={i} position={[Math.cos(ang) * 4.45, 0, Math.sin(ang) * 4.45]}>
                <sphereGeometry args={[0.16, 10, 8]} />
                <meshBasicMaterial color={i % 3 === 0 ? "#F0A020" : "#5EDC7F"} toneMapped={false} />
              </mesh>
            );
          })}
        </group>
        {/* cockpit floor + console */}
        <mesh position={[0, 0.86, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[2.0, 32]} />
          <meshStandardMaterial color="#141A1C" metalness={0.6} roughness={0.5} />
        </mesh>
        <mesh position={[0, 1.4, 1.1]} rotation={[-0.6, 0, 0]}>
          <boxGeometry args={[1.4, 0.08, 0.5]} />
          <meshStandardMaterial color="#5EDC7F" emissive="#5EDC7F" emissiveIntensity={1.2} toneMapped={false} />
        </mesh>
        {/* pilot */}
        <TinyAlien x={0} y={0.86} z={0} seed={4} staticPose baseScale={1.35} />
        {/* glass dome */}
        <mesh ref={domeRef} position={[0, 0.85, 0]} material={M.glass}>
          <sphereGeometry args={[2.05, 32, 18, 0, Math.PI * 2, 0, Math.PI / 2]} />
        </mesh>
        {/* dome frame ribs */}
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[0, 0.85, 0]} rotation={[0, (i / 3) * Math.PI, 0]}>
            <torusGeometry args={[2.07, 0.03, 6, 48, Math.PI]} />
            <meshStandardMaterial color="#C8CCD2" metalness={0.9} roughness={0.3} />
          </mesh>
        ))}
        {/* underside: landing pods + emitter */}
        {[0, 1, 2].map((i) => {
          const ang = (i / 3) * Math.PI * 2 + 0.5;
          return (
            <mesh key={i} position={[Math.cos(ang) * 2.6, -0.62, Math.sin(ang) * 2.6]} material={M.dark}>
              <sphereGeometry args={[0.42, 12, 8, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2]} />
            </mesh>
          );
        })}
        <mesh position={[0, -0.75, 0]}>
          <sphereGeometry args={[0.55, 16, 12]} />
          <meshBasicMaterial color="#F0A020" toneMapped={false} />
        </mesh>
        <mesh position={[0, -0.7, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.9, 0.05, 8, 40]} />
          <meshStandardMaterial color="#F0A020" emissive="#F0A020" emissiveIntensity={1.4} toneMapped={false} />
        </mesh>
        {/* muzzle flash */}
        <mesh ref={beamRef} position={[0, -1.4, 0]} rotation={[Math.PI, 0, 0]} visible={false}>
          <coneGeometry args={[0.6, 1.6, 12]} />
          <meshBasicMaterial color="#F0A020" transparent opacity={0.75} toneMapped={false} depthWrite={false} />
        </mesh>
        <pointLight color="#5EDC7F" intensity={2.4} distance={35} position={[0, -0.5, 0]} />
      </group>
      {/* laser bolt pool */}
      {[...Array(8)].map((_, i) => (
        <group key={i} ref={(el) => (laserRefs.current[i] = el)} visible={false}>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.12, 0.12, 3, 8]} />
            <meshBasicMaterial color="#5EDC7F" transparent opacity={1} toneMapped={false} depthWrite={false} />
          </mesh>
          <mesh position={[0, 0, -1.6]}>
            <sphereGeometry args={[0.25, 10, 8]} />
            <meshBasicMaterial color="#EAFFE8" transparent opacity={1} toneMapped={false} />
          </mesh>
        </group>
      ))}
    </>
  );
}

/* ============================================================
   Asteroid – tumbling, noise-displaced rock (collides with ship)
   ============================================================ */
export function Asteroid({ data }) {
  const groupRef = useRef();
  const meshRef = useRef();
  const registerBirdHit = useWorldStore((s) => s.registerBirdHit);
  const lastHit = useRef(0);
  const M = mats();
  const variant = data.id % 3;
  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    const a = data.angleOffset + t * data.speed;
    const x = Math.cos(a) * data.radius;
    const z = Math.sin(a) * data.radius;
    const y = data.yBase + Math.sin(t * data.yFreq) * data.yAmp;
    groupRef.current.position.set(x, y, z);
    if (meshRef.current) {
      meshRef.current.rotation.x = t * data.tumbleX * 0.3;
      meshRef.current.rotation.y = t * data.tumbleY * 0.4;
    }
    const st = useWorldStore.getState();
    if (!st.spaceMode || st.openId) return;
    const p = st.position;
    const dx = p.x - x;
    const dy = p.y - y;
    const dz = p.z - z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d < 3.5 + data.size && t - lastHit.current > 1.5) {
      lastHit.current = t;
      registerBirdHit();
    }
  });
  return (
    <group ref={groupRef}>
      <mesh ref={meshRef} geometry={ASTEROID_GEOS[variant]} material={M.rock[variant]} scale={data.size} />
    </group>
  );
}

// re-exported for callers that want to build their own lattice pieces
export { mergeAll, barBetween };
